import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import type { FAQ } from '@hellenica/shared';

export default function FAQList() {
  const queryClient = useQueryClient();
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  const { data, isLoading } = useQuery<{ faqs: FAQ[] }>({
    queryKey: ['faqs'],
    queryFn: () => api.get('/v1/admin/faqs'),
  });

  const createMutation = useMutation({
    mutationFn: (data: { question: string; answer: string }) => api.post('/v1/admin/faqs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      setNewFaq({ question: '', answer: '' });
      toast.success('FAQ created');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/faqs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
      toast.success('FAQ deleted');
    },
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">FAQs</h1>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Add New FAQ</h2>
        <div className="space-y-4">
          <input
            type="text"
            value={newFaq.question}
            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
            placeholder="Question"
            className="block w-full rounded-md border-gray-300 shadow-sm"
          />
          <textarea
            value={newFaq.answer}
            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
            placeholder="Answer"
            rows={3}
            className="block w-full rounded-md border-gray-300 shadow-sm"
          />
          <button
            onClick={() => createMutation.mutate(newFaq)}
            disabled={!newFaq.question || !newFaq.answer}
            className="px-4 py-2 bg-brand-bronze text-white rounded-md disabled:opacity-50"
          >
            Add FAQ
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg divide-y">
        {data?.faqs.map((faq) => (
          <div key={faq.id} className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-gray-900">{faq.question}</h3>
                <p className="text-gray-600 mt-2">{faq.answer}</p>
              </div>
              <button
                onClick={() => deleteMutation.mutate(faq.id)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
